import { EDecision, EFinancingProgram } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import { Gavel as GavelIcon, History as HistoryIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material'
import { Button, Divider, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { useGetCreditDecisionOverrideLimits } from '@src/data/api/credit-application/credit-application-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateFinalCreditDecision } from '../../../data/api/final-credit-decision/final-credit-decision-api'
import { useApplySwapApplicants } from '../../../data/api/swap-applicants-roles/swap-applicants-roles-api'
import { useCurrentLang, useFeatureFlags, usePlanEnumForProgram } from '../../../data/selectors/app-config-selectors'
import { useMerchantPaymentPlan } from '../../../data/selectors/credit-selectors'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { Constants, CreditApplication, PersonalLoanWorksheet } from '../../../data/types'
import { CreditDecisionOverrideLimits } from '../../../data/types/CreditDecisionOverrideLimits'
import { FinalCreditDecision } from '../../../data/types/CreditDecisionSchema'
import FormattedMerchantPaymentPlan from '../../ViewCreditApplicationPage/components/FormattedMerchantPaymentPlan'
import CreditDecisionDialog from './creditDecisionDialog'
import DecisonIcon from './decisonIcon'
import SwapApplicantsSimulationDialog from './SwapApplicantsSimulationDialog'

interface Props {
  creditApplication: CreditApplication
  canApprove: boolean
  userCanApproveCreditApplication: boolean
  financingProgramId: EFinancingProgram
  editDisabled: boolean
  userCanOverrideDeclinedDecisions: boolean
  areAllDocumentsRequiredForFinancingApproved: boolean
  openHistoryDialog: () => void
}

const CreditDecisionIceberg = ({
  creditApplication,
  canApprove,
  userCanApproveCreditApplication,
  financingProgramId,
  editDisabled,
  userCanOverrideDeclinedDecisions,
  areAllDocumentsRequiredForFinancingApproved,
  openHistoryDialog,
}: Props) => {
  const [applySwapApplicantsMutate, isApplyingSwap] = useApplySwapApplicants()
  const [updateFinalCreditDecisionMutate, isUpdating, updateError, resetError] = useUpdateFinalCreditDecision()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()

  const [openIceberDecisionDialog, setOpenIceberDecisionDialog] = useState<boolean>(false)
  const [openSwapDialog, setOpenSwapDialog] = useState(false)
  const { t } = useTranslation()

  const { worksheet } = useCreditApplicationData()
  const personalLoanWorksheet = worksheet as PersonalLoanWorksheet | undefined

  const planEnum = usePlanEnumForProgram(creditApplication.financingProgramId)
  const lang = useCurrentLang()
  const merchantPaymentPlan = useMerchantPaymentPlan(creditApplication, personalLoanWorksheet)
  const maxInterestRate = Constants.financingProgramConfigs[creditApplication.financingProgramId].maxInterestRate

  const theme = useTheme()

  const hasNormCreditDecision = creditApplication.normsCreditDecision !== null
  const hasCoapplicant = !!creditApplication.coApplicant

  const featureFlags = useFeatureFlags()

  const canOverrideDeclinedDecisions = userCanOverrideDeclinedDecisions && featureFlags.AllowRefusedOverride

  const [creditDecisionOverrideLimitsData] = useGetCreditDecisionOverrideLimits(
    creditApplication?.financingProgramId,
    creditApplication?.id,
    creditApplication?.normsCreditDecision?.decision !== EDecision.Refused,
  )
  const creditDecisionOverrideLimits = creditDecisionOverrideLimitsData as CreditDecisionOverrideLimits | null

  const handleSaveDecison = useCallback(
    async (value: FinalCreditDecision) => {
      const data = value as FinalCreditDecision & { creditApplicationId: string }
      data.versionTag = creditApplication.versionTag
      data.financingProgramId = creditApplication.financingProgramId
      try {
        await updateFinalCreditDecisionMutate(data)
        await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
        resetError()
        setOpenIceberDecisionDialog(false)
      } catch (error) {
        reportErrorToConsole(error)
        // Keep dialog open to show error banner
      }
    },
    [creditApplication, invalidateFullCreditApplication, updateFinalCreditDecisionMutate, resetError],
  )
  const shoulDisplayCreditDecisionfields =
    Constants.financingProgramConfigs[creditApplication.financingProgramId].useCreditDecisionFields

  return (
    <div>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        alignSelf="center"
      >
        <Typography variant="h6" component="h3" gutterBottom>
          {t('credit.finalDecision')}
        </Typography>
        <IconButton onClick={openHistoryDialog}>
          <HistoryIcon fontSize="large" color="primary" />
        </IconButton>
      </Stack>

      <DecisonIcon decision={creditApplication.finalCreditDecision.decision} />
      {merchantPaymentPlan ? (
        <Stack justifyContent="center" direction="row" sx={{ backgroundColor: theme.palette.action.selected }}>
          <Typography style={{ marginRight: '1rem' }}>Promo:</Typography>
          <FormattedMerchantPaymentPlan merchantPaymentPlan={merchantPaymentPlan} />
        </Stack>
      ) : (
        <Typography
          variant="subtitle1"
          sx={{ backgroundColor: theme.palette.action.selected, mt: theme.spacing(1) }}
          component="p"
        >
          {t('credit.interestRate')} :{' '}
          {creditApplication?.normsCreditDecision == null
            ? maxInterestRate
            : creditApplication.finalCreditDecision.interestRate}{' '}
          %
        </Typography>
      )}
      {shoulDisplayCreditDecisionfields && (
        <>
          <Typography
            variant="subtitle1"
            sx={{ backgroundColor: theme.palette.action.selected, mt: theme.spacing(1) }}
            component="p"
          >
            {t('credit.plan')}:{' '}
            {creditApplication.normsCreditDecision?.planName != null
              ? planEnum[creditApplication.normsCreditDecision.planName ?? ''][lang]
              : 'N/A'}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ backgroundColor: theme.palette.action.selected, mt: theme.spacing(1) }}
            component="p"
          >
            {t('credit.authorizedLoanAmount')}:{' '}
            {formatCurrencyNoDecimals(creditApplication?.finalCreditDecision?.maxAmountFinanced)}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ backgroundColor: theme.palette.action.selected, mt: theme.spacing(1) }}
            component="p"
          >
            {t('credit.maxPayment')}: {formatCurrencyNoDecimals(creditApplication.finalCreditDecision.maxPmtAmount)}
          </Typography>
        </>
      )}
      <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
        <Button
          id="btnEditCreditDecision"
          variant="contained"
          startIcon={<GavelIcon />}
          size="large"
          onClick={() => {
            resetError()
            setOpenIceberDecisionDialog(true)
          }}
          disabled={
            editDisabled ||
            !userCanApproveCreditApplication ||
            (creditApplication.normsCreditDecision?.decision === EDecision.Refused && !canOverrideDeclinedDecisions) ||
            !hasNormCreditDecision
          }
        >
          {t('credit.decision')}
        </Button>
      </Stack>

      {hasCoapplicant && (
        <>
          <Stack direction="row" alignItems="center" sx={{ my: 3 }}>
            <Divider flexItem sx={{ flexGrow: 1 }} />
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <Button
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              size="large"
              onClick={() => {
                setOpenSwapDialog(true)
              }}
            >
              {t('credit.switchOffer')}
            </Button>
          </Stack>
          <SwapApplicantsSimulationDialog
            open={openSwapDialog}
            onClose={() => {
              if (!isApplyingSwap) setOpenSwapDialog(false)
            }}
            onApply={async () => {
              try {
                await applySwapApplicantsMutate({
                  financingProgramId: creditApplication.financingProgramId,
                  creditApplicationId: creditApplication.id,
                })
                await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
                setOpenSwapDialog(false)
              } catch (error) {
                console.error('Error applying simulation:', error)
              }
            }}
            disabled={editDisabled}
            isApplying={isApplyingSwap}
            creditApplication={creditApplication}
          />
        </>
      )}
      {hasNormCreditDecision && (
        <CreditDecisionDialog
          open={openIceberDecisionDialog}
          creditApplicationDecisionOverrideLimits={creditDecisionOverrideLimits}
          canApprove={canApprove}
          financingProgramId={financingProgramId}
          onCancel={() => {
            resetError()
            setOpenIceberDecisionDialog(false)
          }}
          onConfirm={handleSaveDecison}
          creditApplication={creditApplication}
          canOverrideDeclinedDecisions={canOverrideDeclinedDecisions}
          areAllDocumentsRequiredForFinancingApproved={areAllDocumentsRequiredForFinancingApproved}
          apiError={updateError}
          isSubmitting={isUpdating}
        />
      )}
    </div>
  )
}

export default CreditDecisionIceberg
